import { CartItemsComponent } from '@theme/component-cart-items';

/**
 * KALES benefits for Horizon 4.2.0. No gift/cart-line mutations.
 * Discounts are submitted through Horizon's existing cart-discount-component.
 */
const CODE = 'KALES';
const EVENTS = ['shopify:cart:lines-update', 'shopify:cart:discount-update', 'shopify:cart:note-update'];
const states = new Map();
const normaliseCode = (value) => typeof value === 'string' ? value.trim().toUpperCase() : '';
const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

export function discountStatus(cart, code = CODE) {
  if (!cart || typeof cart !== 'object') return 'unknown';
  const codes = cart.discountCodes ?? cart.discount_codes;
  if (Array.isArray(codes)) {
    const entry = codes.find((discount) => normaliseCode(discount?.code) === normaliseCode(code));
    if (entry?.applicable === true) return 'applied';
    if (entry?.applicable === false) return 'rejected';
    if (entry) return 'unknown';
  }
  const allocations = [
    ...(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
    ...(Array.isArray(cart.items) ? cart.items.flatMap((item) =>
      Array.isArray(item.line_level_discount_allocations)
        ? item.line_level_discount_allocations.map((allocation) => allocation.discount_application)
        : []) : []),
  ];
  if (allocations.some((allocation) => allocation?.type === 'discount_code'
    && normaliseCode(allocation.title) === normaliseCode(code))) return 'applied';
  if (Array.isArray(codes)) return 'absent';
  // A complete Ajax cart with actual allocations can reconcile an uncertain previous request.
  if (Array.isArray(cart.items) && Array.isArray(cart.cart_level_discount_applications)
    && Number.isFinite(cart.total_price)) return 'absent';
  return 'unknown';
}

// Shopify Ajax/Liquid money values use hundredths, including CLP.
export const COUPON_MINIMUM_MINOR = 5000000;

/** Product subtotal before KALES, excluding shipping and compare-at prices. */
export function couponSubtotal(cart) {
  if (!cart || cart.currency !== 'CLP' || !Array.isArray(cart.items)
    || !Number.isSafeInteger(cart.items_subtotal_price) || cart.items_subtotal_price < 0) return null;
  let subtotal = cart.items_subtotal_price;
  for (const item of cart.items) {
    for (const allocation of item.line_level_discount_allocations || []) {
      const discount = allocation.discount_application;
      if (discount?.type !== 'discount_code' || normaliseCode(discount.title) !== CODE) continue;
      // items_subtotal_price already excludes line allocations; restore only KALES.
      if (!Number.isSafeInteger(allocation.amount) || allocation.amount < 0) return null;
      subtotal += allocation.amount;
    }
  }
  return Number.isSafeInteger(subtotal) ? subtotal : null;
}

export function couponEligibility(amountMinor, currency = 'CLP') {
  if (currency !== 'CLP' || amountMinor === '' || amountMinor == null) return null;
  const amount = Number(amountMinor);
  if (!Number.isSafeInteger(amount) || amount < 0) return null;
  return { eligible: amount >= COUPON_MINIMUM_MINOR,
    remainingMinor: Math.max(0, COUPON_MINIMUM_MINOR - amount) };
}

export function rewardState(amountMinor) {
  const amount = Number(amountMinor);
  if (!Number.isFinite(amount) || amount < 0) return null;
  const tier = amount >= 12000000 ? 'garment' : amount >= 9000000 ? 'accessory' : 'none';
  return { tier, percent: Math.min(amount / 12000000 * 100, 100),
    remainingMinor: tier === 'none' ? 9000000 - amount : tier === 'accessory' ? 12000000 - amount : 0 };
}

/** Only confirmed, improving user actions may start a celebration. */
export function motionForChange(before, after, kind) {
  if (!before || !after || !rewardState(before.amount) || !rewardState(after.amount)) return null;
  if (kind === 'shopify:cart:discount-update') {
    return before.applied === false && after.applied === true ? 'coupon' : null;
  }
  if (kind !== 'shopify:cart:lines-update' || after.amount <= before.amount) return null;
  const previous = rewardState(before.amount), next = rewardState(after.amount);
  const rank = { none: 0, accessory: 1, garment: 2 };
  if (rank[next.tier] > rank[previous.tier]) return 'reward';
  if (next.remainingMinor > 0 && next.remainingMinor <= 1000000
    && previous.remainingMinor > 1000000) return 'near';
  return null;
}

function withTimeout(promise, milliseconds, message) {
  let timeout;
  const expired = new Promise((_, reject) => {
    timeout = setTimeout(() => reject(new Error(message)), milliseconds);
  });
  return Promise.race([promise, expired]).finally(() => clearTimeout(timeout));
}

class KalesCartBenefits extends HTMLElement {
  static observedAttributes = ['data-reward-amount', 'data-coupon-applied', 'data-coupon-saving', 'data-coupon-subtotal', 'data-coupon-currency', 'data-enabled'];
  #lifecycle;
  #ownPending = false;
  #nativePromise = null;
  #requestedComponent = null;
  #eventRevision = 0;
  #lastQuantityIntent = 0;
  #intentRevision = 0;
  #readController;
  #state;

  connectedCallback() {
    if (this.dataset.enabled !== 'true' || this.#lifecycle) return;
    this.#lifecycle = new AbortController();
    const options = { signal: this.#lifecycle.signal };
    const key = this.dataset.sectionId || 'cart';
    this.#state = states.get(key) || { dismissed: false, status: 'idle', message: '' };
    states.set(key, this.#state);
    if (this.dataset.couponApplied === 'true') {
      this.#state.status = 'applied';
      this.#state.message = 'KALES está aplicado en tu carrito.';
    } else if (this.#state.status === 'applied') {
      this.#state.status = 'idle';
      this.#state.message = '';
    }
    this.addEventListener('click', this.#onClick, options);
    document.addEventListener('quantity-selector:update', () => {
      this.#lastQuantityIntent = Date.now();
      this.#intentRevision += 1;
    }, options);
    for (const name of EVENTS) document.addEventListener(name, this.#onCartUpdate, options);
    this.#render();
  }

  disconnectedCallback() {
    this.#eventRevision += 1;
    this.#readController?.abort();
    this.#lifecycle?.abort();
    this.#lifecycle = null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.#state) return;
    if (name === 'data-coupon-applied' && newValue === 'true') {
      this.#state.status = 'applied';
      this.#state.message = 'KALES está aplicado en tu carrito.';
    } else if (name === 'data-coupon-applied' && !this.#ownPending) {
      this.#state.status = 'idle';
      this.#state.message = '';
    }
    this.#render();
  }

  #onClick = (event) => {
    const action = event.target instanceof Element ? event.target.closest('[data-kales-action]') : null;
    if (!action || !this.contains(action)) return;
    event.preventDefault();
    if (action.dataset.kalesAction === 'close') {
      this.#state.dismissed = true;
      this.#render();
      this.querySelector('[data-kales-action="open"]')?.focus();
    } else if (action.dataset.kalesAction === 'open') {
      this.#state.dismissed = false;
      this.#render();
      this.querySelector('[data-kales-action="close"]')?.focus();
    } else if (action.dataset.kalesAction === 'apply') {
      void this.#applyCode();
    }
  };

  #onCartUpdate = (event) => {
    if (!event.promise || typeof event.promise.then !== 'function') return;
    this.#intentRevision += 1;
    const revision = ++this.#eventRevision;
    if (event.type === 'shopify:cart:discount-update' && this.#ownPending
      && event.target === this.#requestedComponent) this.#nativePromise = event.promise;
    Promise.resolve(event.promise).then((result) => {
      if (!this.isConnected || revision !== this.#eventRevision) return;
      const total = result?.cart?.cost?.totalAmount;
      if (this.dataset.rewardBasis === 'final_price' && total?.currencyCode === 'CLP'
        && total.amount !== '' && Number.isFinite(Number(total.amount))) {
        this.setAttribute('data-reward-amount', String(Math.round(Number(total.amount) * 100)));
      }
      const status = discountStatus(result?.cart);
      if (status === 'applied') {
        this.#state.status = 'applied';
        this.#state.message = 'KALES está aplicado en tu carrito.';
      } else if (!this.#ownPending && (status === 'absent' || status === 'rejected')) {
        this.#state.status = 'idle';
        this.#state.message = '';
      }
      this.#render();
      // Progress uses numeric Liquid attributes updated by Horizon's section morph.
      // Promise resolution can precede the morph, so never infer the amount from old DOM text.
    }).catch(() => {
      if (!this.isConnected || revision !== this.#eventRevision || !this.#ownPending) return;
      this.#setStatus('uncertain', 'No pudimos confirmar el cupón. Revisa el total antes de continuar.');
    });
  };

  async #settleQuantities() {
    const started = Date.now();
    while (this.isConnected) {
      if (Date.now() - started > 12000) throw new Error('El carrito sigue actualizándose. Intenta nuevamente cuando termine.');
      const wait = this.#lastQuantityIntent + 350 - Date.now();
      if (wait > 0) await delay(wait);
      const pending = CartItemsComponent.pendingQuantityUpdates;
      if (pending.length) {
        await withTimeout(Promise.allSettled(pending), 12000, 'El carrito sigue actualizándose.');
        continue;
      }
      if (Date.now() - this.#lastQuantityIntent >= 350) return;
    }
    throw new Error('El carrito cambió. Vuelve a abrirlo para revisar el cupón.');
  }

  #nativeForm() {
    const summary = this.closest('.cart-summary__inner, .cart-drawer__summary');
    const components = summary?.querySelectorAll('cart-discount-component') || [];
    const component = Array.from(components).find((node) => node.dataset.sectionId === this.dataset.sectionId);
    const form = component?.querySelector('form');
    const input = form?.querySelector('input[name="discount"]');
    return { component, form, input };
  }

  async #readCurrentCart() {
    this.#readController = new AbortController();
    const timeout = setTimeout(() => this.#readController?.abort(), 8000);
    const root = globalThis.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(root + 'cart.js', {
        credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json' }, signal: this.#readController.signal,
      });
      if (!response.ok) throw new Error('No pudimos leer el carrito.');
      const cart = await response.json();
      if (!Array.isArray(cart?.items)) throw new Error('No pudimos confirmar el estado del carrito.');
      return cart;
    } finally {
      clearTimeout(timeout);
      this.#readController = null;
    }
  }

  async #applyCode() {
    if (this.#ownPending || this.dataset.couponEnabled !== 'true') return;
    this.#ownPending = true;
    this.#nativePromise = null;
    this.#setStatus('loading', 'Revisando tu carrito…');
    try {
      let cart;
      const readStarted = Date.now();
      while (this.isConnected) {
        await this.#settleQuantities();
        const intent = this.#intentRevision;
        cart = await this.#readCurrentCart();
        if (intent === this.#intentRevision && !CartItemsComponent.pendingQuantityUpdates.length) break;
        if (Date.now() - readStarted > 15000) throw new Error('El carrito sigue cambiando. Revisa el monto y vuelve a intentarlo.');
      }
      if (!this.isConnected) return;
      if (this.dataset.rewardBasis === 'final_price'
        && Number.isFinite(cart.total_price) && cart.total_price >= 0) {
        this.setAttribute('data-reward-amount', String(cart.total_price));
      }
      const subtotal = couponSubtotal(cart);
      this.setAttribute('data-coupon-subtotal', subtotal === null ? '' : String(subtotal));
      this.setAttribute('data-coupon-currency', cart.currency || '');
      const eligibility = couponEligibility(subtotal, cart.currency);
      if (!eligibility) throw new Error('No pudimos confirmar el monto para KALES. Revisa el resumen del carrito.');
      if (!eligibility.eligible) {
        this.#setStatus('rejected', 'KALES requiere al menos $50.000 en productos, sin envío.');
        return;
      }
      const current = discountStatus(cart);
      if (current === 'applied') {
        this.#setStatus('applied', 'KALES ya está aplicado en tu carrito.');
        return;
      }
      if (current === 'unknown') throw new Error('No pudimos confirmar el cupón. Revisa el resumen del carrito.');
      if (cart.items.length === 0) throw new Error('Agrega un producto para usar KALES.');
      const { component, form, input } = this.#nativeForm();
      if (!component || !(form instanceof HTMLFormElement) || !(input instanceof HTMLInputElement)
        || typeof form.requestSubmit !== 'function') {
        throw new Error('Puedes ingresar KALES en el campo de descuento del carrito.');
      }
      if (component.getAttribute('aria-busy') === 'true') {
        throw new Error('Hay un descuento actualizándose. Intenta nuevamente cuando termine.');
      }
      this.#state.message = 'Aplicando KALES…';
      this.#render();
      input.value = CODE;
      this.#nativePromise = null;
      this.#requestedComponent = component;
      form.requestSubmit();
      if (!this.#nativePromise) throw new Error('No pudimos iniciar la aplicación. Puedes usar el campo de descuento.');
      const result = await withTimeout(this.#nativePromise, 12000, 'No pudimos confirmar el cupón. Revisa el total antes de continuar.');
      if (!this.isConnected) return;
      const accepted = discountStatus(result?.cart);
      if (accepted === 'applied') this.#setStatus('applied', 'KALES está aplicado en tu carrito.');
      else if (accepted === 'rejected') this.#setStatus('rejected', 'KALES no se aplicó. Revisa el mínimo de $50.000 en productos y que no haya otro descuento.');
      else this.#setStatus('uncertain', 'No pudimos confirmar el cupón. Revisa el total antes de continuar.');
    } catch (error) {
      if (this.isConnected) this.#setStatus('uncertain', error?.message || 'No pudimos confirmar el cupón. Intenta nuevamente.');
    } finally {
      this.#ownPending = false;
      this.#requestedComponent = null;
      if (this.isConnected) this.#render();
    }
  }

  #setStatus(status, message) {
    this.#state.status = status;
    this.#state.message = message;
    this.#render();
  }

  #render() {
    if (!this.#state || this.dataset.enabled !== 'true') return;
    const card = this.querySelector('[data-kales-coupon]');
    const opener = this.querySelector('[data-kales-opener]');
    if (card) card.hidden = this.#state.dismissed;
    if (opener) opener.hidden = !this.#state.dismissed;
    const applied = this.#state.status === 'applied' || this.dataset.couponApplied === 'true';
    const eligibility = couponEligibility(this.dataset.couponSubtotal, this.dataset.couponCurrency);
    const button = this.querySelector('[data-kales-action="apply"]');
    if (button) {
      button.disabled = this.#ownPending || applied || !eligibility?.eligible;
      button.textContent = this.#ownPending ? 'Un momento…' : applied ? 'KALES aplicado ✓' : eligibility?.eligible ? 'Aplicar KALES' : 'Desde $50.000';
    }
    const money = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
    const status = this.querySelector('[data-kales-status]');
    const saving = Number(this.dataset.couponSaving);
    if (status) status.textContent = applied && saving > 0
      ? 'Ahorraste ' + money.format(saving / 100) + ' con KALES.' : this.#state.message;
    if (status && eligibility && !eligibility.eligible && !this.#ownPending) {
      status.textContent = applied
        ? (saving > 0
          ? 'El carrito muestra ' + money.format(saving / 100) + ' de descuento KALES bajo el mínimo de $50.000. Revisa el total antes de pagar.'
          : 'El carrito informa KALES aplicado bajo el mínimo de $50.000. Revisa el total antes de pagar.')
        : 'Te faltan ' + money.format(eligibility.remainingMinor / 100) + ' en productos para usar KALES.';
    }
    if (button && applied && eligibility && !eligibility.eligible) button.textContent = 'KALES · revisar';
    this.setAttribute('aria-busy', String(this.#ownPending));
    const progress = this.querySelector('[data-kales-progress]');
    if (!progress) return;
    const model = rewardState(this.dataset.rewardAmount);
    if (!model) { progress.hidden = true; return; }
    const bar = this.querySelector('[data-kales-bar]');
    if (bar) {
      bar.style.setProperty('--kales-progress', String(model.percent) + '%');
      bar.setAttribute('aria-valuenow', String(Math.min(Number(this.dataset.rewardAmount), 12000000)));
      bar.setAttribute('aria-valuetext', model.tier === 'garment'
        ? 'Meta completa: $120.000. Regalo máximo desbloqueado'
        : money.format(Number(this.dataset.rewardAmount) / 100));
    }
    const text = this.querySelector('[data-kales-progress-text]');
    if (text) text.textContent = model.tier === 'none'
      ? 'Te faltan ' + money.format(model.remainingMinor / 100) + ' para un accesorio sorpresa.'
      : model.tier === 'accessory'
        ? 'Te faltan ' + money.format(model.remainingMinor / 100) + ' para cambiarlo por una prenda sorpresa.'
        : 'Al finalizar esta compra, recibirás una prenda sorpresa.';
    const title = this.querySelector('[data-kales-reward-title]');
    if (title) title.textContent = model.tier === 'garment' ? '¡Meta alcanzada!'
      : model.tier === 'accessory' ? 'Una sorpresa para ti' : 'Algo lindo te espera';
    const eyebrow = this.querySelector('[data-kales-reward-eyebrow]');
    if (eyebrow) eyebrow.textContent = model.tier === 'garment' ? 'Regalo máximo desbloqueado'
      : model.tier === 'accessory' ? 'Ya alcanzaste tu regalo' : 'Un detalle para ti';
    const note = this.querySelector('[data-kales-reward-note]');
    if (note) note.textContent = model.tier === 'garment'
      ? 'Un regalo por pedido. La prenda reemplaza al accesorio. El monto se calcula después del descuento y sin envío.'
      : 'Un regalo sorpresa por pedido, según el total después del descuento y sin envío. La prenda reemplaza al accesorio; lo incluimos en tu paquete.';
    progress.dataset.tier = model.tier;
  }
}
if (!customElements.get('kales-cart-benefits')) customElements.define('kales-cart-benefits', KalesCartBenefits);

// One document-level listener survives section replacement, including the first
// product added to an empty drawer. It never submits or changes the cart.
let motionRevision = 0;
let stopWaiting = () => {};
const seenPromises = new WeakSet();
const visible = (node) => typeof node.checkVisibility === 'function'
  ? node.checkVisibility({ checkVisibilityCSS: true, checkOpacity: true })
  : node.getClientRects().length > 0;
const hosts = () => Array.from(document.querySelectorAll('kales-cart-benefits[data-enabled="true"]'));

function readMotionBaseline() {
  const components = hosts();
  const host = components.find(visible) || components[0];
  if (host && host.dataset.rewardAmount !== '' && Number.isFinite(Number(host.dataset.rewardAmount))) {
    return { amount: Number(host.dataset.rewardAmount), applied: host.dataset.couponApplied === 'true' };
  }
  // Explicit empty-cart markup is evidence of zero; missing markup alone is not.
  return document.querySelector('[data-cart-drawer-empty]') ? { amount: 0, applied: false } : null;
}

function presentMotion(kind, after, revision) {
  if (globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  if (typeof globalThis.IntersectionObserver !== 'function') return;
  const className = kind === 'coupon' ? 'is-coupon-celebrating'
    : kind === 'reward' ? 'is-reward-celebrating' : 'is-reward-near';
  const selector = kind === 'coupon' ? '[data-kales-coupon]' : '[data-kales-progress]';
  let settleTimer, expiryTimer, startedAt = null;
  const animatedNodes = new Set();
  const anchors = new Set();
  const exposure = new WeakMap();
  const cleanup = () => {
    clearTimeout(settleTimer); clearTimeout(expiryTimer); observer.disconnect();
    viewportObserver.disconnect();
    for (const node of animatedNodes) {
      node.classList.remove(className);
      node.style.removeProperty('--kales-motion-age');
    }
  };
  const flush = () => {
    if (revision !== motionRevision) { cleanup(); return; }
    const candidates = hosts().filter((host) => Number(host.dataset.rewardAmount) === after.amount
      && (kind !== 'coupon' || host.dataset.couponApplied === 'true'))
      .map((host) => host.querySelector(selector)).filter(Boolean);
    const targets = candidates.filter((node) => {
      const anchor = kind === 'coupon' ? node : node.querySelector('.kales-rewards__status') || node;
      if (!anchors.has(anchor)) {
        anchors.add(anchor);
        viewportObserver.observe(anchor);
      }
      const drawer = node.closest('dialog');
      const drawerReady = !drawer || (drawer.open
        && !drawer.classList.contains('theme-drawer__dialog--opening')
        && !drawer.classList.contains('theme-drawer__dialog--opening-inline-start')
        && !drawer.classList.contains('theme-drawer__dialog--closing'));
      return drawerReady && visible(node) && exposure.get(anchor) === true;
    });
    if (!targets.length) return;
    if (startedAt === null) {
      startedAt = Date.now();
      clearTimeout(expiryTimer);
      expiryTimer = setTimeout(cleanup, 3100);
    }
    const elapsed = Date.now() - startedAt;
    if (elapsed >= 2800) return;
    for (const node of targets) {
      if (node.classList.contains(className)) continue;
      node.classList.remove('is-coupon-celebrating', 'is-reward-celebrating', 'is-reward-near');
      node.style.setProperty('--kales-motion-age', String(-elapsed) + 'ms');
      void node.offsetWidth;
      node.classList.add(className);
      animatedNodes.add(node);
    }
  };
  const schedule = () => { clearTimeout(settleTimer); settleTimer = setTimeout(flush, 180); };
  // CSS visibility is not enough: nested drawer scroll areas can clip the card.
  // Start only when its meaningful content is actually in view, not on opening.
  const viewportObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) exposure.set(entry.target, entry.isIntersecting && entry.intersectionRatio >= 0.9);
    schedule();
  }, { threshold: [0, 0.9] });
  // Survive a late Horizon morph without restarting: recover a removed class
  // or replacement node at the elapsed point in the same animation timeline.
  const observer = new MutationObserver(schedule);
  observer.observe(document.body, { subtree: true, childList: true, attributes: true,
    attributeFilter: ['data-reward-amount', 'data-coupon-applied', 'data-coupon-saving', 'class', 'open', 'hidden'] });
  stopWaiting = cleanup;
  expiryTimer = setTimeout(cleanup, 60000);
  schedule();
}

for (const eventName of EVENTS.slice(0, 2)) document.addEventListener(eventName, (event) => {
  if (!event.promise || typeof event.promise.then !== 'function' || seenPromises.has(event.promise)) return;
  seenPromises.add(event.promise);
  const before = readMotionBaseline();
  const revision = ++motionRevision;
  stopWaiting();
  Promise.resolve(event.promise).then(async (result) => {
    if (revision !== motionRevision || result?.detail?.didError || result?.userErrors?.length) return;
    const total = result?.cart?.cost?.totalAmount;
    if (total?.currencyCode !== 'CLP' || total.amount === '' || !Number.isFinite(Number(total.amount))) return;
    const status = discountStatus(result.cart);
    const after = { amount: Math.round(Number(total.amount) * 100),
      applied: status === 'applied' ? true : status === 'absent' || status === 'rejected' ? false : null };
    const kind = motionForChange(before, after, eventName);
    if (!kind) return;
    await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
    if (revision === motionRevision) presentMotion(kind, after, revision);
  }).catch(() => { /* Native cart UI owns error feedback; errors never celebrate. */ });
});
